// Feb 18, 2026 — Unified search bar (Phase 1).
//
// Match-quality search across People (creators) + Silos. No popularity
// signal. Honors per-handle visibility toggles on the creator side.
//
// Used both as a global compact bar in nav and as the expanded surface
// inside the FanPortal "Discover" section.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, X, UserCircle, Books, ArrowSquareOut, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getToken } from "@/lib/api";
import { copyText } from "@/lib/io";
import SiloArt from "@/components/SiloArt";

// Oct 2026 (Randy) — "I want the art to show up left of the name in this drop
// down too." Same order as his profile circle: an uploaded avatar wins, then
// the person's own top silo art, then the plain mark.
const Thumb = ({ art, avatar, fallback, rounded }) => (
  <div className={`w-9 h-9 ${rounded} bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center shrink-0 overflow-hidden`}>
    {avatar
      ? <img src={avatar} alt="" className="w-full h-full object-cover" />
      : art || fallback}
  </div>
);

function useDebouncedValue(value, delay = 220) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export const SearchBar = ({ variant = "compact", placeholder, autoFocus = false, interestsOnly = false }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ people: [], silos: [], empty: true });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [refCode, setRefCode] = useState(null);
  const wrapRef = useRef(null);
  const debounced = useDebouncedValue(q.trim());

  // Pull this user's own ref_code for the invite-link fallback. Anon visitors
  // get a generic /signup link instead.
  useEffect(() => {
    if (!getToken()) return;
    api.get("/auth/me").then((r) => {
      setRefCode(r.data?.referral_code || null);
    }).catch(() => { /* not fatal */ });
    // referral_code lives on UserPublic; if missing, the invite block falls back.
    api.get("/referrals/me").then((r) => {
      if (r.data?.referral_code) setRefCode(r.data.referral_code);
    }).catch(() => { /* not fatal */ });
  }, []);

  useEffect(() => {
    if (!debounced || debounced.length < 2) {
      setResults({ people: [], silos: [], empty: true });
      return;
    }
    let cancel = false;
    setLoading(true);
    api.get(`/search?q=${encodeURIComponent(debounced)}${interestsOnly ? "&interests_only=1" : ""}`)
      .then((r) => {
        if (cancel) return;
        setResults(r.data || { people: [], silos: [], empty: true });
      })
      .catch(() => { if (!cancel) setResults({ people: [], silos: [], empty: true }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [debounced, interestsOnly]);

  // Close on outside click (compact variant only — the inline expanded
  // variant inside FanPortal stays open as long as it's mounted).
  useEffect(() => {
    if (variant !== "compact") return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [variant]);

  const inviteOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const inviteUrl = refCode ? `${inviteOrigin}/?ref=${refCode}` : `${inviteOrigin}/signup`;
  const copyInvite = async () => {
    const ok = await copyText(inviteUrl);
    toast[ok ? "success" : "error"](ok ? "Invite link copied — paste it wherever you talk to them." : "Couldn't copy. Try again?");
  };

  const isInline = variant === "inline";
  const showPanel = isInline ? (debounced.length >= 2) : (open && debounced.length >= 2);

  return (
    <div ref={wrapRef} className={`relative ${isInline ? "w-full" : "w-full max-w-md"}`} data-testid="search-bar-wrap">
      <div className={`flex items-center gap-2 bg-white border hairline rounded-md ${isInline ? "px-4 py-3" : "px-3 py-2"} focus-within:border-[#4A5D4E]`}>
        <MagnifyingGlass size={isInline ? 18 : 16} weight="duotone" className="text-[#6B6A68] shrink-0" />
        <input
          autoFocus={autoFocus}
          data-testid="search-input"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder={placeholder || "Search people or silos…"}
          className={`flex-1 bg-transparent outline-none ${isInline ? "text-base" : "text-sm"} text-[#1C1B1A] placeholder:text-[#A09E9B]`}
        />
        {q && (
          <button onClick={() => { setQ(""); setResults({ people: [], silos: [], empty: true }); }} data-testid="search-clear" className="text-[#6B6A68] hover:text-[#1C1B1A]">
            <X size={14} />
          </button>
        )}
      </div>

      {showPanel && (
        <div
          data-testid="search-panel"
          className={isInline
            ? "mt-4 bg-white border hairline rounded-xl p-5"
            : "absolute top-full left-0 right-0 mt-2 bg-white border hairline rounded-xl shadow-lg max-h-[70vh] overflow-y-auto p-5 z-30"
          }
        >
          {loading && (
            <div className="text-xs text-[#6B6A68]">Searching…</div>
          )}

          {!loading && results.empty && (
            <div className="text-sm text-[#1C1B1A]" data-testid="search-empty">
              <div className="font-serif-display text-lg">
                {interestsOnly ? "Nothing here matches your interests." : "No one by that name yet."}
              </div>
              <p className="text-xs text-[#6B6A68] mt-2 leading-relaxed">
                {interestsOnly
                  ? "Turn \u201cShow only Defined interests\u201d off to search the whole platform \u2014 or add the topic above."
                  : "Couldn't find them on My Silo. Want to invite them?"}
              </p>
              <button
                type="button"
                onClick={copyInvite}
                data-testid="search-empty-copy-invite"
                className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-md border hairline text-xs text-[#1C1B1A] hover:border-[#4A5D4E]"
              >
                <Copy size={12} weight="duotone" /> Copy invite link
              </button>
              <div className="text-[10px] text-[#A09E9B] mt-2 break-all">{inviteUrl}</div>
            </div>
          )}

          {!loading && !results.empty && (
            <div className="space-y-5">
              {results.people.length > 0 && (
                <div data-testid="search-people-group">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] mb-2 flex items-center gap-1.5">
                    <UserCircle size={11} weight="duotone" /> People · {results.people.length}
                  </div>
                  <ul className="space-y-1">
                    {results.people.map((p) => (
                      <li key={p.id}>
                        <Link
                          to={`/u/${p.ref_code}`}
                          data-testid={`search-person-${p.ref_code}`}
                          className="flex items-start gap-3 p-2 rounded-md hover:bg-[#F0F1EE] transition-colors"
                        >
                          <Thumb
                            rounded="rounded-full"
                            avatar={p.avatar_url}
                            art={p.art_enabled && p.cube
                              ? <SiloArt cube={p.cube} enabled rounded={false} className="w-full h-full" data-testid={`search-person-art-${p.ref_code}`} />
                              : null}
                            fallback={<UserCircle size={18} weight="duotone" />}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-[#1C1B1A] truncate">{p.display_name}</div>
                            {p.bio && <div className="text-xs text-[#6B6A68] truncate">{p.bio}</div>}
                            {p.matched_field && p.matched_field !== "name" && p.matched_value && (
                              <div className="text-[10px] text-[#4A5D4E] mt-0.5">
                                via {p.matched_field}: @{p.matched_value}
                              </div>
                            )}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {results.silos.length > 0 && (
                <div data-testid="search-silos-group">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] mb-2 flex items-center gap-1.5">
                    <Books size={11} weight="duotone" /> Silos · {results.silos.length}
                  </div>
                  <ul className="space-y-1">
                    {results.silos.map((s) => (
                      <li key={s.id}>
                        <Link
                          to={`/s/${s.slug}`}
                          data-testid={`search-silo-${s.slug}`}
                          className="flex items-start gap-3 p-2 rounded-md hover:bg-[#F0F1EE] transition-colors"
                        >
                          <Thumb
                            rounded="rounded-md"
                            art={s.art_enabled && s.cube
                              ? <SiloArt cube={s.cube} enabled rounded={false} className="w-full h-full" data-testid={`search-silo-art-${s.slug}`} />
                              : null}
                            fallback={<Books size={16} weight="duotone" />}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-[#1C1B1A] truncate flex items-center gap-1.5">
                              {s.name} <ArrowSquareOut size={10} weight="duotone" className="text-[#6B6A68]" />
                            </div>
                            {s.tagline && <div className="text-xs text-[#6B6A68] truncate">{s.tagline}</div>}
                            {s.creator_name && (
                              <div className="text-[10px] text-[#6B6A68] mt-0.5">by {s.creator_name}</div>
                            )}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
